import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { authService } from '../../services/authService';
import { logger } from '../../utils/logger';

interface VerificationFormProps {
  email: string;
  onSuccess?: () => void;
}

const VerificationForm: React.FC<VerificationFormProps> = ({ email, onSuccess }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await authService.verifyEmail(email, code);

      setSuccessMessage('Email verificado com sucesso! Redirecionando...');

      const alreadySeenWelcome = typeof window !== 'undefined' && window.localStorage.getItem('welcome_new_user_seen');
      setTimeout(() => {
        if (alreadySeenWelcome) {
          navigate('/login?message=Email verificado com sucesso! Faça login para continuar.');
        } else {
          navigate(`/welcome-new-user?from=register&email=${encodeURIComponent(email)}`);
        }
      }, 1000);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao verificar código. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    setSuccessMessage('');

    try {
      logger.log('[VerificationForm] Reenviando código para:', email);
      const response = await authService.resendVerificationEmail(email);
      logger.log('[VerificationForm] Resposta do reenvio:', response);
      setSuccessMessage(response.message || 'Código reenviado! Verifique seu email.');
    } catch (err) {
      logger.error('[VerificationForm] Erro ao reenviar código:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao reenviar código. Tente novamente.');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <p className="text-green-800 dark:text-green-200 text-sm">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-brand-error-bg border border-brand-error-border rounded-lg">
          <p className="text-brand-error-text text-sm">{error}</p>
        </div>
      )}

      <div className="mb-6 p-4 bg-background border border-border rounded-lg">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-brand-text-secondary">
            <p className="font-medium mb-1">Código enviado para {email}</p>
            <p>Digite o código de verificação que foi enviado para seu email.</p>
            <p className="mt-2 text-xs text-brand-text-muted">
              O código é válido por 24 horas.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="code" className="block text-sm font-medium text-brand-text-primary mb-2">
          Código de Verificação <span className="text-red-500">*</span>
        </label>
        <input
          id="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 text-center text-2xl tracking-widest"
          placeholder="000000"
          maxLength={6}
          required
          disabled={isLoading}
        />
      </div>

      <Button
        type="submit"
        variant="hero"
        size="lg"
        disabled={isLoading || code.length !== 6}
        className="w-full text-lg px-8 py-6"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Verificando...
          </>
        ) : (
          'Verificar Código'
        )}
      </Button>

      <div className="text-center text-brand-text-muted text-sm mt-4">
        <p>
          Não recebeu o código?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="text-brand-accent hover:text-brand-accent-hover font-semibold hover:underline transition-colors duration-200 disabled:opacity-50"
          >
            {isResending ? 'Reenviando...' : 'Reenviar código'}
          </button>
        </p>
      </div>
    </form>
  );
};

export default VerificationForm;

